import React from 'react'
import Footer from '~components/organisms/Footer'
import withStyles from '@material-ui/core/styles/withStyles'
import { CSSTransition, TransitionGroup } from 'react-transition-group'
import PageProgress from '~shared/components/PageProgress'
import GridLines from '~components/molecules/GridLines'
import Colors from '~shared/assets/styles/colors'
import { AccountProvider } from '~src/AccountContext'
import { AppFrameProvider, withAppFrame } from '~shared/components/AppFrameContext'
import Onboarding from '~components/organisms/Onboarding'
import withWidth from '@material-ui/core/withWidth'
import { BottomBar } from '~src/new/widgets/BottomBar'
import { Menu } from '~src/new/widgets/Menu'
import { Header } from '~src/new/widgets/Header'
import { TopMessageLine } from '~src/new/widgets/TopMessageLine'
import classNames from 'classnames'
import { ModalSheetProvider } from '~src/new/app/providers/ModalSheetProvider'
import { DrawerProvider, useDrawer } from '~src/new/app/providers/DrawerProvider'
import { Drawer } from '~src/new/widgets/Drawer'

const MOBILE_NAVIGATION_MAX_SCREEN_WIDTH = 767

const isLikelyMobileScreen = () => {
  if (typeof window === 'undefined') return false

  const screenWidth = window.screen?.width || window.innerWidth
  const screenHeight = window.screen?.height || window.innerHeight

  return Math.min(screenWidth, screenHeight) <= MOBILE_NAVIGATION_MAX_SCREEN_WIDTH
}

const styles = ({ breakpoints }) => ({
  layout: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr auto',
    gridTemplateRows: 'auto 1fr',
    gridTemplateAreas: `
      "header header header"
      "sidebar content drawer"
    `,
    height: '100vh',
    width: '100vw',
    overflow: 'hidden',
    backgroundColor: '#1c1c1c',
    position: 'relative',

    [breakpoints.down('sm')]: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      width: '100%',
      overflow: 'unset',
      backgroundColor: 'transparent',
    },
  },
  container: {
    position: 'relative',
    minHeight: '100vh',
    zIndex: 0,
    '@media print': {
      display: 'block',
    },
  },
  containerBGWhite: {
    backgroundColor: Colors.White,
  },
  contentWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gridArea: 'content',
    height: '100%',
    width: '100%',
    backgroundColor: '#f3f3f3',
    overflow: 'auto',
    position: 'relative',
    transition: 'border-radius 300ms ease',
  },
  borderTopLeftRadius: {
    borderTopLeftRadius: '24px',

    [breakpoints.down('sm')]: {
      borderTopLeftRadius: '0px',
    },
  },
  borderTopRightRadius: {
    borderTopRightRadius: '24px',

    [breakpoints.down('sm')]: {
      borderTopRightRadius: '0px',
    },
  },
  content: {
    width: '100%',
    flex: 1,
    // maxWidth: '1000px',
    // maxHeight: '100%',
    // overflow: 'auto',
    '@media print': {
      backgroundColor: Colors.White,
    },
  },
  footer: {
    width: '100%',
  },
  mainMenu: {
    height: '100%',
  },

  fadeEnter: {
    opacity: 0,
    // transform: `translate3d(0, ${-unit * 2}px, 0)`,
    clipPath: 'circle(0px at center)',
  },
  fadeEnterActive: {
    opacity: 1,
    // transform: 'translate3d(0,0,0)',
    clipPath: 'circle(100% at center)',
    transition: `opacity 350ms 100ms,
      clip-path 350ms ease-in`,
  },
  loader: {
    position: 'absolute',
    width: '100%',
    zIndex: 9999,
  },
  header: {
    gridArea: 'header',
    marginTop: 'auto',
    zIndex: 1000,
  },
  sidebar: {
    gridArea: 'sidebar',
  },
})

const ContentWrapper = ({ children, classes, showSidebar }) => {
  const { isOpen } = useDrawer()

  return (
    <div
      id="content-wrapper"
      className={classNames(classes.contentWrapper, {
        [classes.borderTopLeftRadius]: showSidebar,
        [classes.borderTopRightRadius]: isOpen,
      })}
    >
      {children}
    </div>
  )
}

const AppFrame = withStyles(styles)(
  withWidth()(
    class AppFrame extends React.Component {
      state = {
        onboardingIndex: null,
        isSidebarCollapsed: true,
      }

      static getDerivedStateFromProps(props) {
        if (props.data) {
          const { header: { items = [] } = {} } = props.data
          let xsMenuItems = []
          items.forEach(item => {
            if (!item.props.xsIconVisible) {
              xsMenuItems = [...xsMenuItems, { ...item.props, _link: item._link }]
            }
          })
          return { data: props.data, xsMenuItems }
        }

        return {}
      }

      componentDidMount() {
        this._isMounted = true
        if (process.env.NODE_ENV === 'development') {
          window.addEventListener('keyup', this.onKeyUp)
        }
        if (this.props.isPrint) {
          document.body.classList.add('print')
        }
      }

      componentDidUpdate(prevProps) {
        if (prevProps.isPrint !== this.props.isPrint) {
          if (this.props.isPrint) {
            document.body.classList.add('print')
          } else {
            document.body.classList.remove('print')
          }
        }
      }

      componentWillUnmount() {
        this._isMounted = false
        document.body.classList.remove('print')
        window.removeEventListener('keyup', this.onKeyUp)
      }

      onKeyUp = e => {
        if (e.keyCode === 71 && e.altKey) {
          this.setState({ showGrid: !this.state.showGrid })
        }
      }

      handleAccountChange = selectedAccountId => {
        const { api } = this.props
        api.changeSelectedAccount({ selectedAccountId })
      }

      render() {
        const {
          children,
          classes,
          busy,
          routeKey,
          contentKey,
          member,
          history,
          isPrint,
          showLoadBar,
          componentName,
          width,
          storeDataProps: { store } = {},
          isPopupOpen,
        } = this.props

        const { isFakePopupOpen } = store
        const { accounts, selectedAccountId } = member || {}
        const { data, showGrid, onboardingIndex, isSidebarCollapsed } = this.state

        if (!data) {
          return null
        }

        const {
          header,
          menu,
          footer,
          bottomBar,
          onboarding = [],
          settings: {
            general: { backgroundLoginImage } = {},
            technical: { captchaVisibility } = {},
            labels,
          } = {},
        } = data

        const isMobileLayout = (width === 'xs' || width === 'sm') && isLikelyMobileScreen()
        const showDrawer = !isMobileLayout

        const selectedAccount = accounts?.find(({ _id }) => _id === selectedAccountId)

        const selectedAccountIsArchive = selectedAccount?.accountStatus !== 'active'

        const hasBottomBar = !isPrint && isMobileLayout && bottomBar?.length > 0
        const bottomBarOffset = hasBottomBar ? 75 : undefined

        let availableMenu = menu && [...menu]
        if (selectedAccountIsArchive && availableMenu) {
          const availableMenuItems = [
            '/profile',
            '/rewards',
            '/faqs',
            'integration_instructions',
            '/brands',
            '/products',
            '/contact',
            '/consumer-trials',
          ]
          availableMenu = availableMenu.filter(
            ({ to, icon }) => availableMenuItems.includes(to) || availableMenuItems.includes(icon),
          )
        }

        const isDashboard = componentName === 'Dashboard'
        const withBackground = componentName === 'LoginPage' && backgroundLoginImage

        const showSidebar = !isPrint && !isMobileLayout && availableMenu?.length > 0

        onboarding?.find((data, i) => {
          const show =
            !data.paths ||
            (data.paths &&
              (data.paths.includes(window.location.pathname) ||
                (window.location.hash && data.paths.includes(window.location.hash))))
          if (show && onboardingIndex !== i) this.setState({ onboardingIndex: i })
          return show
        })

        return (
          <AppFrameProvider value={data}>
            <AccountProvider
              value={{ accounts, selectedAccountId, onAccountChange: this.handleAccountChange }}
            >
              <ModalSheetProvider>
                <DrawerProvider>
                  <div id="layout" className={classes.layout}>
                    {!isFakePopupOpen && (
                      <PageProgress visible={showLoadBar || busy} className={classes.loader} />
                    )}

                    {showGrid && <GridLines />}

                    {!isPrint && header && !isFakePopupOpen && (
                      <Header
                        isLoading={showLoadBar || busy}
                        isMobileLayout={isMobileLayout}
                        className={classes.header}
                        history={history}
                        items={header.items}
                        menuItems={availableMenu}
                      />
                    )}

                    {showSidebar && (
                      <Menu
                        className={classes.sidebar}
                        items={availableMenu}
                        activePath={history?.location?.pathname}
                        collapsed={isSidebarCollapsed}
                        onCollapsedChange={next => this.setState({ isSidebarCollapsed: next })}
                      />
                    )}

                    <ContentWrapper classes={classes} showSidebar={showSidebar}>
                      <TopMessageLine labels={labels} contentComponentName={componentName} />
                      <TransitionGroup className={classes.content}>
                        <CSSTransition
                          key={routeKey}
                          timeout={{ enter: 450, exit: 0 }}
                          style={{ height: '100%' }}
                          classNames={{
                            enter: classes.fadeEnter,
                            enterActive: classes.fadeEnterActive,
                          }}
                        >
                          <div key={contentKey}>{children}</div>
                        </CSSTransition>
                      </TransitionGroup>

                      {!isPrint && footer && !withBackground && (
                        <Footer
                          labels={labels}
                          className={classes.footer}
                          data={footer}
                          captchaBrandingVisibility={captchaVisibility}
                          isDashboard={isDashboard}
                          style={{ paddingBottom: bottomBarOffset }}
                        />
                      )}
                    </ContentWrapper>
                    {showDrawer && <Drawer />}
                    {hasBottomBar && !isPopupOpen && !isFakePopupOpen && (
                      <BottomBar items={bottomBar || []} activePath={history?.location?.pathname} />
                    )}
                    {!isPrint &&
                      onboarding[onboardingIndex] !== undefined &&
                      ((!isPopupOpen && !isFakePopupOpen) ||
                        onboarding[onboardingIndex]?.isPopup) && (
                        <Onboarding
                          data={onboarding[onboardingIndex]}
                          onFinish={() => {
                            onboarding.splice(onboardingIndex, 1)
                            this.setState({ onboardingIndex: null })
                          }}
                        />
                      )}
                  </div>
                </DrawerProvider>
              </ModalSheetProvider>
            </AccountProvider>
          </AppFrameProvider>
        )
      }
    },
  ),
)

export default AppFrame

export { withAppFrame }
